import React, { useState, useEffect } from 'react';
import { Category } from '../../types/category';
import { useWebCategories } from '../../hooks/useWebCategories';
import { XMarkIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

interface WebCategoryFormProps {
  category: Category | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const WebCategoryForm: React.FC<WebCategoryFormProps> = ({ category, onClose, onSuccess }) => {
  const { mainCategories, addCategory, updateCategory } = useWebCategories();
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    parentId: null as string | null,
    isActive: true,
    showInWebshop: true,
    order: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);

  // Slug generálás a név alapján
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Ékezetek eltávolítása
      .replace(/[^a-z0-9\s-]/g, '') // Csak betűk, számok, szóközök és kötőjelek
      .trim()
      .replace(/\s+/g, '-') // Szóközök helyett kötőjel
      .replace(/-+/g, '-'); // Többszörös kötőjelek egyszerűsítése
  };

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        slug: category.slug || generateSlug(category.name),
        parentId: category.parentId,
        isActive: category.isActive,
        showInWebshop: category.showInWebshop,
        order: (category as any).order || 0
      });
      setAutoGenerateSlug(!category.slug); // Ha már van slug, ne generáljunk automatikusan
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (category) {
        await updateCategory(category.id, formData);
      } else {
        await addCategory(formData);
      }
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 transform transition-all">
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-20 rounded-lg p-2">
                <GlobeAltIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">
                {category ? 'Kategória szerkesztése' : 'Új webshop kategória'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <p className="text-blue-100 text-sm mt-2">
            {category ? 'Módosítsd a kategória adatait' : 'Hozz létre egy új kategóriát a webshop számára'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Kategória neve *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                const newName = e.target.value;
                setFormData({ 
                  ...formData, 
                  name: newName,
                  slug: autoGenerateSlug ? generateSlug(newName) : formData.slug
                });
              }}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="pl. Laptopok, Kiegészítők..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              URL slug
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => {
                  setFormData({ ...formData, slug: e.target.value });
                  setAutoGenerateSlug(false);
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="pl. laptopok, kiegeszitok"
              />
              <label className="flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={autoGenerateSlug}
                  onChange={(e) => {
                    setAutoGenerateSlug(e.target.checked);
                    if (e.target.checked) {
                      setFormData({ ...formData, slug: generateSlug(formData.name) });
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                />
                Automatikus generálás a név alapján
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Szülő kategória
            </label>
            <select
              value={formData.parentId || ''}
              onChange={(e) => setFormData({ ...formData, parentId: e.target.value || null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Nincs (főkategória)</option>
              {mainCategories.map((cat) => (
                <option key={cat.id} value={cat.id} disabled={cat.id === category?.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sorrend
            </label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Aktív</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.showInWebshop}
                onChange={(e) => setFormData({ ...formData, showInWebshop: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Webshopban látható</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Mentés...
                </span>
              ) : 'Mentés'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebCategoryForm;
